/**
 * SEO Component for managing meta tags
 * Use this on every page to set proper SEO tags
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  noindex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title = 'HomeQuest Tech - AI-Powered Construction Management Platform',
  description = 'Transform your construction business with AI-powered project management. Get instant site compliance, automated communications, smart vendor bidding, and real-time collaboration. 2-week free trial.',
  keywords = 'construction management software, AI construction platform, project management, site compliance, GIS analysis, contractor software, builder app, construction automation, vendor management, construction AI',
  ogImage = 'https://construction-platform-7pispgmw7-ken-whites-projects-cbf8a7e8.vercel.app/og-image.jpg',
  ogType = 'website',
  canonical,
  noindex = false,
}) => {
  const siteUrl = 'https://construction-platform-7pispgmw7-ken-whites-projects-cbf8a7e8.vercel.app';
  const fullTitle = title.includes('HomeQuest') ? title : `${title} | HomeQuest Tech`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={`${siteUrl}${canonical}`} />}

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical ? `${siteUrl}${canonical}` : siteUrl} />
      <meta property="og:site_name" content="HomeQuest Tech" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO */}
      <meta name="author" content="HomeQuest Tech" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />

      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'HomeQuest Tech',
          'applicationCategory': 'BusinessApplication',
          'description': description,
          'operatingSystem': 'Web, iOS, Android',
          'offers': {
            '@type': 'Offer',
            'price': '149',
            'priceCurrency': 'USD',
            'priceValidUntil': '2025-12-31',
          },
          'aggregateRating': {
            '@type': 'AggregateRating',
            'ratingValue': '5',
            'reviewCount': '47',
          },
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
